import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  BookOpen, 
  Users, 
  Clock, 
  CheckCircle2, 
  ArrowRight,
  MessageCircle,
  Award,
  Globe,
  Zap,
  Heart,
  HelpCircle,
  Video,
  Target,
  Bell,
  ListTodo,
  Timer
} from "lucide-react";
import { Link } from "react-router-dom";

const OpenStudy = () => {
  const faqData = [
    {
      question: "What happened to OpenStudy?",
      answer: "OpenStudy was acquired by Brainly in 2015-2016 after facing sustainability challenges. The platform that helped over 50 million learners with peer-to-peer academic help is no longer available as an independent service."
    },
    {
      question: "Is CSW a good OpenStudy alternative?",
      answer: "Yes! CSW offers real-time collaborative study sessions where you can ask questions and get help from peers, similar to OpenStudy's community-driven approach. CSW adds live video study rooms, focus timers, and task management for a complete study experience."
    },
    {
      question: "Can I get homework help on CSW like I did on OpenStudy?",
      answer: "While CSW focuses on live study sessions rather than Q&A forums, you can join subject-specific study rooms where peers often help each other. The real-time interaction can be even more effective than asynchronous Q&A."
    },
    {
      question: "Is CSW free like OpenStudy was?",
      answer: "Yes! CSW offers free access to core features including Study Stream (live video study rooms), Study Timer (Pomodoro-style focus sessions), and Task Management. Premium features are available for enhanced functionality."
    },
    {
      question: "How is CSW different from Brainly?",
      answer: "While Brainly focuses on homework Q&A, CSW emphasizes live collaborative study sessions. With CSW, you can study alongside others in real-time via video, use focus timers together, and build consistent study habits with streaks and achievements."
    },
    {
      question: "Can I form study groups on CSW?",
      answer: "Absolutely! CSW's Group Study feature lets you create private study rooms, invite friends, and study together with shared focus sessions. It's perfect for exam prep or ongoing study partnerships."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "OpenStudy Alternative - Free Peer Study Platform",
    "description": "Looking for an OpenStudy alternative? OpenStudy was acquired but CSW offers free Study Stream, Study Timer, and collaborative learning features.",
    "url": "https://csw.live/openstudy/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Pair Study & Work",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "OpenStudy",
      "applicationCategory": "EducationalApplication"
    },
    "mentions": [
      {
        "@type": "SoftwareApplication",
        "name": "CSW",
        "url": "https://csw.live"
      },
      {
        "@type": "SoftwareApplication",
        "name": "Brainly",
        "url": "https://brainly.com"
      }
    ],
    "keywords": "OpenStudy alternative, peer learning platform, homework help, study community, collaborative learning"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CSW - OpenStudy Alternative",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Live video study rooms",
      "Real-time peer collaboration",
      "Pomodoro study timer",
      "Task management system",
      "Study streaks and achievements",
      "Group study sessions",
      "Focus reminders",
      "Cross-platform access"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Pair Study & Work",
      "url": "https://csw.live"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>OpenStudy Alternative - Free Peer Study Platform | CSW</title>
        <meta name="description" content="Looking for an OpenStudy alternative? OpenStudy was acquired by Brainly but CSW offers free Study Stream, Study Timer, and Task Management for collaborative learning." />
        <meta name="keywords" content="OpenStudy alternative, OpenStudy replacement, peer learning platform, homework help alternative, study community, Brainly alternative" />
        <link rel="canonical" href="https://csw.live/openstudy/" />
        <meta property="og:title" content="OpenStudy Alternative - Free Peer Study Platform" />
        <meta property="og:description" content="OpenStudy was acquired but CSW continues the peer learning mission with free study rooms, timers, and collaborative features." />
        <meta property="og:url" content="https://csw.live/openstudy/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OpenStudy Alternative - Free Peer Study Platform" />
        <meta name="twitter:description" content="OpenStudy was acquired but CSW continues the peer learning mission with free study rooms and collaborative features." />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-6 text-primary border-primary">
                <BookOpen className="w-3 h-3 mr-1" />
                OpenStudy Alternative
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                OpenStudy is Gone, But Peer Learning Lives On
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                OpenStudy was <a href="https://brainly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">acquired by Brainly</a>, but CSW continues the mission of collaborative learning with free <Link to="/study-with-me/" className="text-primary hover:underline">study rooms</Link>, focus tools, and a supportive community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="gap-2" asChild>
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Start Studying Free <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/openstudy/history/">
                    Read OpenStudy's Story
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Shutdown Notice */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-card rounded-2xl p-8 border shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <Heart className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold mb-2">What Happened to OpenStudy?</h2>
                    <p className="text-muted-foreground mb-4">
                      OpenStudy, the pioneering peer learning platform founded in 2007, was <a href="https://brainly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">acquired by Brainly</a> in 2015-2016 after serving over 50 million learners worldwide. The platform that made it possible to get homework help from peers at any hour is no longer available.
                    </p>
                    <p className="text-muted-foreground">
                      Looking for a similar experience? CSW offers <Link to="/online-workspace/" className="text-primary hover:underline">online workspaces</Link>, <Link to="/productivity-tool/" className="text-primary hover:underline">productivity tools</Link>, and <Link to="/study-with-friends/" className="text-primary hover:underline">collaborative study features</Link> that continue OpenStudy's legacy of peer learning.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Everything OpenStudy Offered, and More
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                CSW takes peer learning to the next level with live collaboration tools
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <div className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors">
                <div className="p-3 bg-primary/10 rounded-xl w-fit mb-4">
                  <Video className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Live Study Rooms</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Join <Link to="/studystream/" className="text-primary hover:underline">video study rooms</Link> with peers worldwide. Better than async Q&A – get real-time collaboration and support.
                </p>
                <Badge variant="secondary" className="text-xs">Replaces Q&A Forums</Badge>
              </div>
              
              <div className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors">
                <div className="p-3 bg-secondary/10 rounded-xl w-fit mb-4">
                  <Timer className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Focus Timer</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Use <Link to="/study-timer/" className="text-primary hover:underline">Pomodoro-style timers</Link> to stay focused. Study in sync with others for accountability.
                </p>
                <Badge variant="secondary" className="text-xs">New Feature</Badge>
              </div>

              <div className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors">
                <div className="p-3 bg-primary/10 rounded-xl w-fit mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Peer Community</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Connect with <Link to="/group-study/" className="text-primary hover:underline">study groups</Link> and find accountability partners for your academic journey.
                </p>
                <Badge variant="secondary" className="text-xs">Like OpenStudy</Badge>
              </div>

              <div className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors">
                <div className="p-3 bg-secondary/10 rounded-xl w-fit mb-4">
                  <ListTodo className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Organize assignments with <Link to="/task-management/" className="text-primary hover:underline">task lists</Link>. Track progress and never miss a deadline.
                </p>
                <Badge variant="secondary" className="text-xs">New Feature</Badge>
              </div>

              <div className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors">
                <div className="p-3 bg-primary/10 rounded-xl w-fit mb-4">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Achievements & Streaks</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Earn badges and maintain study streaks. Gamification that rewards consistency, just like OpenStudy's points system.
                </p>
                <Badge variant="secondary" className="text-xs">Like OpenStudy</Badge>
              </div>

              <div className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors">
                <div className="p-3 bg-secondary/10 rounded-xl w-fit mb-4">
                  <Bell className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Smart Reminders</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Get <Link to="/reminders/" className="text-primary hover:underline">personalized reminders</Link> to keep your study schedule on track.
                </p>
                <Badge variant="secondary" className="text-xs">New Feature</Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Why CSW Section */}
        <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Why Students Choose CSW After OpenStudy
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Real-Time Collaboration</h3>
                      <p className="text-sm text-muted-foreground">Instead of waiting for Q&A responses, study live with peers who can help instantly.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Sustainable Platform</h3>
                      <p className="text-sm text-muted-foreground">CSW is built to last, avoiding the sustainability issues that led to OpenStudy's closure.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Complete Study Suite</h3>
                      <p className="text-sm text-muted-foreground">Timers, tasks, rooms, and reminders – everything you need in one place.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Global Community</h3>
                      <p className="text-sm text-muted-foreground">Join learners from around the world, just like OpenStudy's 50M+ user community.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Free Core Features</h3>
                      <p className="text-sm text-muted-foreground">Access study rooms, timers, and task management without paying anything.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold mb-1">Modern Interface</h3>
                      <p className="text-sm text-muted-foreground">Clean, distraction-free design built for today's students.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison with Brainly */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  CSW vs Brainly: Different Approaches to Learning
                </h2>
                <p className="text-muted-foreground">
                  <a href="https://brainly.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Brainly</a> acquired OpenStudy's assets, but CSW offers a different, complementary approach
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl p-6 border">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-muted-foreground" />
                    Brainly
                  </h3>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Q&A focused – post questions, get answers
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Asynchronous help from community
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Points system for contributors
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      Best for specific homework questions
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 border border-primary/20">
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    CSW
                  </h3>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Live study sessions – collaborate in real-time
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Video rooms for face-to-face peer support
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Focus tools: timer, tasks, reminders
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      Best for sustained study sessions
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full mb-4">
                  <HelpCircle className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Frequently Asked Questions</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">
                  OpenStudy Alternative FAQ
                </h2>
              </div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-xl border px-6">
                    <AccordionTrigger className="text-left hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center">Learn More About Effective Studying</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/blog/science-behind-study-with-me/" className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors group">
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">The Science Behind Study With Me</h3>
                  <p className="text-sm text-muted-foreground">Discover why studying with peers improves focus and retention.</p>
                </Link>
                <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors group">
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">Camera On = Productivity Up</h3>
                  <p className="text-sm text-muted-foreground">How video accountability transforms your study sessions.</p>
                </Link>
                <Link to="/blog/top-10-digital-tools-for-academic-success/" className="bg-card rounded-xl p-6 border hover:border-primary/50 transition-colors group">
                  <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">Top 10 Digital Study Tools</h3>
                  <p className="text-sm text-muted-foreground">The best apps and platforms for academic success in 2024.</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Continue Your Peer Learning Journey?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              OpenStudy connected millions of learners. CSW is here to continue that legacy with modern, real-time collaboration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="gap-2" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Join CSW Free <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/openstudy/history/">
                  OpenStudy History
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OpenStudy;
